import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import React from "react";
import { apiPost, apiPut, apiRequestNoLoadder, apiGetBlob } from "../../utils/api_service";
import { API_URL } from "../../constants/api";
import { ShowToast, toastType } from "../../utils/toast-service";
import { IsSectionVisible } from "../../utils/helper";
import { SECTIONS } from "../../constants/appConstants";
import camelcaseKeys from "camelcase-keys";
import ConfirmDialog from "../common/Dailogbox/confirmDailog";

class EditBuildingPhoto extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      categories: [],
      // full details loaded from GetBuildingImageById
      details: null,
      isLoading: true,
      // editable fields (initialized empty, populated after details load)
      categoryId: "",
      caption: "",
      internalNotes: "",
      isPrimary: false,
      isSubmitting: false,
      isReplacing: false,
      showSetPrimaryConfirm: false,
    };
    this.replaceFileInputRef = React.createRef();
  }

  componentDidMount() {
    const { photo } = this.props;

    // Fetch categories and full image details in parallel
    Promise.all([
      apiRequestNoLoadder({
        url: API_URL.Building.getImageCategoryMaster,
        params: { businessLineId: photo ? photo.businessLineId || 1 : 1 },
      }),
      apiRequestNoLoadder({
        url: `${API_URL.Building.getBuildingImageDetails}/${photo.buildingImageId}`,
      }),
    ]).then(([catRes, detailRes]) => {
      const categories = catRes.data || [];
      const details = detailRes.data ? camelcaseKeys(detailRes.data) : null;

      this.setState({
        categories,
        details,
        isLoading: false,
        categoryId: details ? details.categoryId || "" : "",
        caption: details ? details.caption || "" : "",
        internalNotes: details ? details.internalNotes || "" : "",
        isPrimary: details ? !!details.isPrimary : false,
      });
    }).catch(() => {
      this.setState({ isLoading: false });
      ShowToast("Failed to load image details.", toastType.ERROR);
    });
  }

  handleDownload = async () => {
    const { details } = this.state;
    if (!details?.buildingImageId) return;
    const { success, data: blob } = await apiGetBlob({
      url: `${API_URL.Building.downloadBuildingImage}/${details.buildingImageId}`,
    });
    if (!success || !blob) {
      ShowToast("Download failed. Please try again.", toastType.ERROR);
      return;
    }
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = details.fileName || "building-image";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
  };

  handleReplaceClick = () => {
    this.replaceFileInputRef.current.click();
  };

  handleReplaceFileChange = (e) => {
    const file = e.target.files[0];
    e.target.value = null;
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      ShowToast("Only image files (JPEG, JPG, PNG) are allowed.", toastType.ERROR);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      ShowToast("File size must not exceed 10 MB.", toastType.ERROR);
      return;
    }

    const { photo, onSuccess } = this.props;
    const { details } = this.state;

    const formData = new FormData();
    formData.append("FormFile", file);
    formData.append("BuildingImageId", photo.buildingImageId);
    formData.append("BuildingId", details ? details.buildingId : photo.buildingId);
    formData.append("BusinessLineId", details ? details.businessLineId : photo.businessLineId || 1);

    this.setState({ isReplacing: true });
    apiPost({ url: API_URL.Building.replaceBuildingImage, postBody: formData })
      .then(({ success }) => {
        this.setState({ isReplacing: false });
        if (success) {
          ShowToast("Image replaced successfully.", toastType.SUCCESS);
          onSuccess && onSuccess();
        } else {
          ShowToast("Replace failed. Please try again.", toastType.ERROR);
        }
      })
      .catch(() => {
        this.setState({ isReplacing: false });
        ShowToast("An error occurred during replace.", toastType.ERROR);
      });
  };

  handleSubmit = () => {
    const { categoryId, isPrimary } = this.state;
    const { details } = this.state;

    if (!categoryId) {
      ShowToast("Please select a category.", toastType.ERROR);
      return;
    }

    // If setting as primary on a non-primary image, confirm first
    if (isPrimary && !details?.isPrimary) {
      this.setState({ showSetPrimaryConfirm: true });
      return;
    }

    this.doSubmit();
  };

  doSubmit = () => {
    const { photo, onSuccess } = this.props;
    const { categoryId, caption, internalNotes, isPrimary } = this.state;

    const formData = new FormData();
    formData.append("BuildingImageId", photo.buildingImageId);
    formData.append("CategoryId", categoryId);
    formData.append("Caption", caption || "");
    formData.append("InternalNotes", internalNotes || "");
    formData.append("IsPrimary", isPrimary);

    this.setState({ isSubmitting: true });
    apiPut(API_URL.Building.updateBuildingImageMetadata, formData)
      .then(({ success }) => {
        this.setState({ isSubmitting: false });
        if (success) {
          onSuccess && onSuccess();
        } else {
          ShowToast("Update failed. Please try again.", toastType.ERROR);
        }
      })
      .catch(() => {
        this.setState({ isSubmitting: false });
        ShowToast("An error occurred while saving.", toastType.ERROR);
      });
  };

  render() {
    const { categories, details, isLoading, categoryId, caption, internalNotes, isPrimary, isSubmitting, isReplacing, showSetPrimaryConfirm } = this.state;
    const { photo, onCancel } = this.props;

    const canEdit = IsSectionVisible(SECTIONS.Building_Photos, SECTIONS.Building_Photos_Listing, SECTIONS.ADD_EDIT);
    const canDownload = IsSectionVisible(SECTIONS.Building_Photos, SECTIONS.Building_Photo_Download);

    const webImageUrl = details && details.webUrl ? details.webUrl : null;

    if (isLoading) {
      return (
        <div style={{ display: "flex", justifyContent: "center", padding: "48px" }}>
          <CircularProgress size={32} />
        </div>
      );
    }

    return (
      <>
        {showSetPrimaryConfirm && (
          <ConfirmDialog
            IsOpen={showSetPrimaryConfirm}
            Message="This will set this image as the primary image for the property. Do you want to proceed?"
            OnHandelClick={(confirmed) => {
              this.setState({ showSetPrimaryConfirm: false });
              if (confirmed) this.doSubmit();
            }}
            AgreeText="Yes, Set as Primary"
            DisAgreeText="Cancel"
          />
        )}

        <div>
          {/* Web image preview + download + replace buttons */}
        {webImageUrl && (
          <div style={{ marginBottom: "16px" }}>
            <input
              type="file"
              ref={this.replaceFileInputRef}
              hidden
              accept="image/jpeg,image/jpg,image/png"
              onChange={this.handleReplaceFileChange}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "2px", marginBottom: "4px" }}>
              {canEdit && (
                <Tooltip title="Replace image">
                  <Button
                    className="action-button no-print"
                    size="small"
                    onClick={this.handleReplaceClick}
                    disabled={isReplacing}
                  >
                    <span className="tool-icon-text">{isReplacing ? "Replacing..." : "Replace"}</span>
                  </Button>
                </Tooltip>
              )}
              {canDownload && (
                <Tooltip title="Download original">
                  <Button
                    className="action-button no-print"
                    size="small"
                    onClick={this.handleDownload}
                  >
                    <span className="tool-icon-text">Download</span>
                  </Button>
                </Tooltip>
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <img
                src={webImageUrl}
                alt={photo.caption || photo.fileName}
                style={{
                  maxWidth: "100%",
                  objectFit: "contain",
                }}
              />
            </div>
            <Typography variant="caption" display="block" color="textSecondary" style={{ marginTop: "4px" }}>
              {photo.fileName}
            </Typography>
          </div>
        )}

        {/* Readonly building context */}
        {details && (
          <div style={{ marginBottom: "8px" }}>
            {/* Row 1: CoStar ID + Building Name (same total width as category below) */}
            <div style={{ display: "flex", gap: "16px" }}>
              <TextField
                label="CoStar ID"
                value={details.coStarPropId || ""}
                style={{ width: "140px", flexShrink: 0 }}
                size="small"
                margin="dense"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                InputProps={{ readOnly: true, style: { color: "#9e9e9e" } }}
              />
              <TextField
                label="Building Name"
                value={details.buildingName || ""}
                style={{ width: "1085px", flexShrink: 0 }}
                size="small"
                margin="dense"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                InputProps={{ readOnly: true, style: { color: "#9e9e9e" } }}
              />
            </div>
            {/* Row 2: Address (full width, same as category) */}
            <TextField
              label="Address"
              value={details.add1 || ""}
              fullWidth
              size="small"
              margin="dense"
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              InputProps={{ readOnly: true, style: { color: "#9e9e9e" } }}
            />
          </div>
        )}

        {/* Category */}
        <TextField
          select
          label={<span>Category <span style={{ color: "#f44336" }}>*</span></span>}
          value={categoryId}
          onChange={(e) => this.setState({ categoryId: e.target.value })}
          fullWidth
          size="small"
          margin="dense"
          variant="outlined"
          InputLabelProps={{ shrink: true, style: { color: "rgba(0,0,0,0.6)" } }}
          SelectProps={{ style: { fontSize: 13 } }}
          inputProps={{ readOnly: !canEdit }}
          disabled={!canEdit}
        >
          {categories.map((c) => (
            <MenuItem key={c.categoryId} value={c.categoryId}>
              {c.categoryName}
            </MenuItem>
          ))}
        </TextField>

        {/* Caption */}
        <TextField
          label="Caption"
          value={caption}
          onChange={(e) => this.setState({ caption: e.target.value })}
          fullWidth
          size="small"
          margin="dense"
          variant="outlined"
          InputLabelProps={{ shrink: true, style: { color: "rgba(0,0,0,0.6)" } }}
          inputProps={{ maxLength: 500, readOnly: !canEdit }}
          disabled={!canEdit}
        />

        {/* Internal Notes */}
        <TextField
          label="Internal Notes"
          value={internalNotes}
          onChange={(e) => this.setState({ internalNotes: e.target.value })}
          fullWidth
          size="small"
          margin="dense"
          variant="outlined"
          InputLabelProps={{ shrink: true, style: { color: "rgba(0,0,0,0.6)" } }}
          multiline
          rows={3}
          inputProps={{ maxLength: 1000, readOnly: !canEdit }}
          disabled={!canEdit}
        />

        {/* Is Primary */}
        <FormControlLabel
          control={
            <Checkbox
              checked={isPrimary}
              onChange={(e) => this.setState({ isPrimary: e.target.checked })}
              color="primary"
              size="small"
              disabled={!canEdit}
            />
          }
          label={<span style={{ fontSize: 14 }}>Set as Primary Photo</span>}
        />

        {/* Actions */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "2px", marginTop: "16px" }}>
          <Button className="action-button no-print" size="small" onClick={onCancel} disabled={isSubmitting}>
            <span className="tool-icon-text">Cancel</span>
          </Button>
          {canEdit && (
            <Button
              className="action-button no-print"
              size="small"
              onClick={this.handleSubmit}
              disabled={isSubmitting}
            >
              <span className="tool-icon-text">{isSubmitting ? "Saving..." : "Save"}</span>
            </Button>
          )}
        </div>
      </div>
      </>
    );
  }
}

export default EditBuildingPhoto;


